import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CheckCircle2, Database, Download, PauseCircle, PlayCircle, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { channelApi } from '../config/api';
import { useAuth } from '../context/AuthContext';

const field = 'h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400';
const tones = {
  HEALTHY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  WARNING: 'bg-amber-50 text-amber-700 border-amber-200',
  ERROR: 'bg-red-50 text-red-700 border-red-200',
  UNKNOWN: 'bg-slate-50 text-slate-600 border-slate-200',
  SUCCESS: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FAILED: 'bg-red-50 text-red-700 border-red-200',
  PREVIEW_READY: 'bg-blue-50 text-blue-700 border-blue-200',
  PARTIAL_SUCCESS: 'bg-amber-50 text-amber-700 border-amber-200',
  ANOMALY_DETECTED: 'bg-orange-50 text-orange-700 border-orange-200',
  REQUIRES_REVIEW: 'bg-orange-50 text-orange-700 border-orange-200',
  DRIFT_DETECTED: 'bg-amber-50 text-amber-700 border-amber-200',
  SCHEDULED: 'bg-blue-50 text-blue-700 border-blue-200',
  HYBRID: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  MANUAL: 'bg-slate-50 text-slate-600 border-slate-200',
  MATCH: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DIFFERENT: 'bg-amber-50 text-amber-700 border-amber-200',
  MISSING_IN_PRIME: 'bg-blue-50 text-blue-700 border-blue-200',
  MISSING_IN_SOURCE: 'bg-orange-50 text-orange-700 border-orange-200',
  UNMAPPED: 'bg-red-50 text-red-700 border-red-200',
  INVALID: 'bg-red-50 text-red-700 border-red-200',
  DUPLICATE: 'bg-red-50 text-red-700 border-red-200',
};

function normalizeAdminStatus(user) {
  return String(user?.admin_status || user?.adminStatus || user?.role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Badge({ value }) {
  const text = value || 'UNKNOWN';
  return <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${tones[text] || tones.UNKNOWN}`}>{text}</span>;
}

function Metric({ label, value, Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div><div className="text-[11px] font-bold uppercase text-slate-500">{label}</div><div className="mt-2 text-2xl font-bold text-slate-900">{value ?? '-'}</div></div>
        <Icon className="h-5 w-5 text-slate-500" />
      </div>
    </div>
  );
}

function extractError(e) {
  return e.response?.data?.error?.message || e.response?.data?.message || e.message || 'Request failed';
}

function MemberList({ data, summary, needsAttention, loading, onOpen, onTest, onPreview }) {
  const [query, setQuery] = React.useState('');
  const [status, setStatus] = React.useState('ALL');
  const [transport, setTransport] = React.useState('ALL');
  const [environment, setEnvironment] = React.useState('ALL');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((item) => {
      const hay = [item.member?.code, item.member?.name, item.transport, item.status, item.health].filter(Boolean).join(' ').toLowerCase();
      const statusOk = status === 'ALL'
        || (status === 'CONNECTED' && item.connectionStatus === 'CONFIGURED')
        || (status === 'INTEGRATION' && ['CONFIGURATION', 'CONNECTIVITY', 'MAPPING', 'STAGING_TEST'].includes(item.status))
        || item.status === status || item.health === status;
      return (!q || hay.includes(q))
        && statusOk
        && (transport === 'ALL' || String(item.transport || '').toLowerCase().includes(transport.toLowerCase()))
        && (environment === 'ALL' || item.environment === environment);
    });
  }, [data, query, status, transport, environment]);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Members" value={summary?.members} Icon={Database} />
        <Metric label="Connected" value={summary?.connected} Icon={CheckCircle2} />
        <Metric label="Integration Process" value={summary?.inProcess} Icon={RefreshCw} />
        <Metric label="Issue" value={summary?.issue} Icon={AlertTriangle} />
        <Metric label="Products Synced" value={summary?.products} Icon={Database} />
        <Metric label="Scheduled Jobs" value={summary?.scheduled} Icon={CalendarClock} />
        <Metric label="Drift Issues" value={summary?.drift} Icon={AlertTriangle} />
        <Metric label="Unresolved Mapping" value={summary?.unresolved} Icon={AlertTriangle} />
        <Metric label="Failed Sync" value={summary?.failedSync} Icon={AlertTriangle} />
        <Metric label="Last Sync" value={fmtDate(summary?.lastSync)} Icon={ShieldCheck} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 text-sm font-bold text-slate-900">Needs Attention</div>
        {needsAttention?.length ? (
          <div className="grid gap-2 md:grid-cols-2">
            {needsAttention.map((item) => (
              <button key={item.member.code} type="button" onClick={() => onOpen(item.member.code)} className="rounded-md border border-slate-200 p-3 text-left text-sm hover:bg-slate-50">
                <div className="font-bold">{item.member.code}</div>
                <div className="text-slate-500">{item.reason}</div>
              </button>
            ))}
          </div>
        ) : <div className="text-sm text-slate-500">No member integration currently needs attention.</div>}
      </div>

      <div className="grid gap-3 lg:grid-cols-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className={`${field} w-full pl-9`} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Member" />
        </div>
        <select className={field} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="CONNECTED">Connected</option>
          <option value="INTEGRATION">Integration</option>
          <option value="ERROR">Issue</option>
          <option value="NOT_STARTED">Not Started</option>
        </select>
        <select className={field} value={transport} onChange={(e) => setTransport(e.target.value)}>
          <option value="ALL">All Transport</option>
          <option value="REST">REST</option>
          <option value="Webhook">Webhook</option>
          <option value="FTP">FTP/SFTP</option>
          <option value="XML">XML</option>
        </select>
        <select className={field} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
          <option value="ALL">All Environment</option>
          <option value="DEV">DEV</option>
          <option value="STAGING">STAGING</option>
          <option value="PRODUCTION">PRODUCTION</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Transport</th><th className="px-4 py-3">Environment</th><th className="px-4 py-3">Products</th><th className="px-4 py-3">Last Sync</th><th className="px-4 py-3">Last Result</th><th className="px-4 py-3">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-500">Loading member integrations...</td></tr> : filtered.map((item) => (
              <tr key={item.member.code}>
                <td className="px-4 py-3"><div className="font-bold">{item.member.code}</div><div className="text-xs text-slate-500">{item.member.name}</div></td>
                <td className="px-4 py-3"><Badge value={item.health} /></td>
                <td className="px-4 py-3">{item.transport || '-'}</td>
                <td className="px-4 py-3">{item.environment || '-'}</td>
                <td className="px-4 py-3">{item.stats?.product_count ?? '-'}</td>
                <td className="px-4 py-3">{fmtDate(item.timestamps?.last_sync_at)}</td>
                <td className="px-4 py-3">{item.lastResult || '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => onOpen(item.member.code)} className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold hover:bg-slate-50">Open</button>
                    <button onClick={() => onTest(item.member.code)} disabled={!item.connectionTestSupported} className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40">Test Connection</button>
                    <button onClick={() => onPreview(item.member.code)} disabled={!item.previewSupported} className="rounded-md bg-[#12284A] px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Preview Sync</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">Scheduled Jobs</div>
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">Job</th><th className="px-4 py-3">Frequency</th><th className="px-4 py-3">Last Run</th><th className="px-4 py-3">Next Run</th><th className="px-4 py-3">Last Result</th><th className="px-4 py-3">Failures</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-500">No scheduled jobs to show.</td></tr> : filtered.map((item) => {
              const s = item.schedule || {};
              return (
                <tr key={`schedule-${item.member.code}`}>
                  <td className="px-4 py-3"><div className="font-bold">{item.member.code}</div><div className="text-xs text-slate-500">{item.member.name}</div></td>
                  <td className="px-4 py-3">{s.sync_type || 'PRODUCT_SYNC'}</td>
                  <td className="px-4 py-3">{s.frequency_type || 'HOURLY'}{s.interval_minutes ? ` (${s.interval_minutes}m)` : ''}</td>
                  <td className="px-4 py-3">{fmtDate(s.last_run_at)}</td>
                  <td className="px-4 py-3">{s.is_enabled ? fmtDate(s.next_run_at) : '-'}</td>
                  <td className="px-4 py-3">{s.last_result || '-'}</td>
                  <td className="px-4 py-3">{s.consecutive_failure_count || 0}</td>
                  <td className="px-4 py-3"><Badge value={s.is_enabled ? s.sync_mode : 'Disabled'} /></td>
                  <td className="px-4 py-3"><button onClick={() => onOpen(item.member.code)} className="text-xs font-bold text-[#12284A]">View History</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreviewTable({ preview }) {
  const [filter, setFilter] = React.useState('ALL');
  const [selected, setSelected] = React.useState(null);
  const items = preview?.items || [];
  const visible = filter === 'ALL' ? items : items.filter((item) => item.status === filter);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-7">
        {[
          ['Received', preview?.counts?.received],
          ['Valid', preview?.counts?.valid],
          ['Invalid', preview?.counts?.invalid],
          ['Unmapped', preview?.counts?.unmapped],
          ['New', preview?.counts?.new],
          ['Update', preview?.counts?.update],
          ['Unchanged', preview?.counts?.unchanged],
        ].map(([k, v]) => <div key={k} className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[11px] font-bold uppercase text-slate-500">{k}</div><div className="text-xl font-bold">{v ?? '-'}</div></div>)}
      </div>
      <div className="flex flex-wrap gap-2">
        {['ALL', 'NEW', 'UPDATE', 'UNCHANGED', 'INVALID', 'UNMAPPED', 'POTENTIAL_DEACTIVATION'].map((key) => <button key={key} onClick={() => setFilter(key)} className={`rounded-md border px-2.5 py-1.5 text-xs font-bold ${filter === key ? 'border-[#12284A] bg-[#12284A] text-white' : 'border-slate-200 text-slate-600'}`}>{key}</button>)}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Status</th><th className="px-4 py-3">External Code</th><th className="px-4 py-3">Product Name</th><th className="px-4 py-3">Member</th><th className="px-4 py-3">Current PRIME Value</th><th className="px-4 py-3">Incoming Value</th><th className="px-4 py-3">Issue</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No preview rows for this filter.</td></tr> : visible.map((item, idx) => (
              <tr key={`${item.externalProductId}-${idx}`} onClick={() => setSelected(item)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-4 py-3"><Badge value={item.status} /></td>
                <td className="px-4 py-3">{item.externalCode || item.externalProductId || '-'}</td>
                <td className="px-4 py-3">{item.productName || '-'}</td>
                <td className="px-4 py-3">{item.memberCode}</td>
                <td className="px-4 py-3">{item.current?.price ?? item.current?.name ?? '-'}</td>
                <td className="px-4 py-3">{item.incoming?.price ?? item.incoming?.name ?? '-'}</td>
                <td className="px-4 py-3">{item.issue || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between"><div className="font-bold">Product {selected.externalCode || selected.externalProductId}</div><button onClick={() => setSelected(null)} className="text-sm font-semibold text-slate-500">Close</button></div>
          {selected.diff?.length ? selected.diff.map((d) => <div key={d.field} className="grid grid-cols-3 gap-3 border-t border-slate-100 py-2 text-sm"><b>{d.field}</b><span>{String(d.current ?? '-')}</span><span className="font-semibold text-[#12284A]">{String(d.incoming ?? '-')}</span></div>) : <div className="text-sm text-slate-500">No field changes for this row.</div>}
        </div>
      )}
    </div>
  );
}

function SchedulePanel({ schedule, busy, onSave, onPause, onResume, onRunNow }) {
  const [form, setForm] = React.useState(schedule || {});
  React.useEffect(() => setForm(schedule || {}), [schedule]);
  const enabled = Boolean(form.is_enabled);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Status" value={enabled ? form.sync_mode : 'Disabled'} Icon={CalendarClock} />
        <Metric label="Last Run" value={fmtDate(form.last_run_at)} Icon={RefreshCw} />
        <Metric label="Last Success" value={fmtDate(form.last_success_at)} Icon={CheckCircle2} />
        <Metric label="Next Run" value={enabled ? fmtDate(form.next_run_at) : '-'} Icon={ShieldCheck} />
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm font-semibold text-slate-700">Sync Mode<select className={`${field} mt-1 w-full`} value={form.sync_mode || 'MANUAL'} onChange={(e) => setForm({ ...form, sync_mode: e.target.value, is_enabled: e.target.value === 'MANUAL' ? false : form.is_enabled })}><option>MANUAL</option><option>SCHEDULED</option><option>HYBRID</option></select></label>
          <label className="text-sm font-semibold text-slate-700">Frequency<select className={`${field} mt-1 w-full`} value={form.frequency_type || 'HOURLY'} onChange={(e) => setForm({ ...form, frequency_type: e.target.value })}><option value="EVERY_15_MINUTES">Every 15 minutes</option><option value="EVERY_30_MINUTES">Every 30 minutes</option><option value="HOURLY">Every 1 hour</option><option value="EVERY_3_HOURS">Every 3 hours</option><option value="EVERY_6_HOURS">Every 6 hours</option><option value="EVERY_12_HOURS">Every 12 hours</option><option value="DAILY">Daily</option><option value="CUSTOM_CRON">Custom Cron</option></select></label>
          <label className="text-sm font-semibold text-slate-700">Timezone<input className={`${field} mt-1 w-full`} value={form.timezone || 'Asia/Jakarta'} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></label>
          <label className="flex items-center gap-2 pt-6 text-sm font-semibold text-slate-700"><input type="checkbox" checked={enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />Enable Scheduled Sync</label>
          {form.frequency_type === 'CUSTOM_CRON' && <label className="text-sm font-semibold text-slate-700 lg:col-span-2">Cron Expression<input className={`${field} mt-1 w-full font-mono`} value={form.cron_expression || ''} onChange={(e) => setForm({ ...form, cron_expression: e.target.value })} placeholder="0 */3 * * *" /></label>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button disabled={busy} onClick={() => onSave(form)} className="rounded-md bg-[#12284A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">Save Schedule</button>
          <button disabled={busy} onClick={onRunNow} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold disabled:opacity-40"><PlayCircle className="h-4 w-4" />Run Now</button>
          {enabled ? <button disabled={busy} onClick={onPause} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold disabled:opacity-40"><PauseCircle className="h-4 w-4" />Pause</button> : <button disabled={busy} onClick={onResume} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold disabled:opacity-40">Resume</button>}
        </div>
      </div>
    </div>
  );
}

function ReconciliationPanel({ latest, history, items, busy, onRun, onLoad }) {
  const [filter, setFilter] = React.useState('ALL');
  const [selected, setSelected] = React.useState(null);
  const rows = filter === 'ALL' ? items : items.filter((item) => item.status === filter);
  const exportCsv = () => {
    const header = 'member,external_product_id,product_code,status,field,source_value,prime_value,issue';
    const body = (items || []).flatMap((item) => (item.diff?.length ? item.diff : [{ field: '', incoming: '', current: '' }]).map((d) => [
      latest?.member_code || '', item.externalProductId || '', item.productCode || '', item.status || '', d.field || '',
      JSON.stringify(d.incoming ?? item.source ?? ''), JSON.stringify(d.current ?? item.prime ?? ''), item.issue || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob([[header, body].filter(Boolean).join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `prime-reconciliation-${latest?.member_code || 'member'}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Last Reconciliation" value={fmtDate(latest?.started_at || latest?.created_at)} Icon={RefreshCw} />
        <Metric label="Status" value={latest?.status || '-'} Icon={ShieldCheck} />
        <Metric label="Source Count" value={latest?.source_count} Icon={Database} />
        <Metric label="PRIME Count" value={latest?.prime_count} Icon={Database} />
        <Metric label="Match Rate" value={latest?.match_rate != null ? `${latest.match_rate}%` : '-'} Icon={CheckCircle2} />
        <Metric label="Differences" value={latest?.different_count || 0} Icon={AlertTriangle} />
        <Metric label="Missing" value={(latest?.missing_prime_count || 0) + (latest?.missing_source_count || 0)} Icon={AlertTriangle} />
        <Metric label="Issues" value={(latest?.unmapped_count || 0) + (latest?.invalid_count || 0) + (latest?.duplicate_count || 0)} Icon={AlertTriangle} />
      </div>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={onRun} className="rounded-md bg-[#12284A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">Run Reconciliation</button>
        <button disabled={!items?.length} onClick={exportCsv} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold disabled:opacity-40"><Download className="h-4 w-4" />Export Result</button>
      </div>
      <div className="flex flex-wrap gap-2">{['ALL', 'DIFFERENT', 'MISSING_IN_PRIME', 'MISSING_IN_SOURCE', 'UNMAPPED', 'INVALID', 'DUPLICATE', 'MATCH'].map((key) => <button key={key} onClick={() => setFilter(key)} className={`rounded-md border px-2.5 py-1.5 text-xs font-bold ${filter === key ? 'border-[#12284A] bg-[#12284A] text-white' : 'border-slate-200 text-slate-600'}`}>{key}</button>)}</div>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[920px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Status</th><th className="px-4 py-3">Product Code</th><th className="px-4 py-3">Product Name</th><th className="px-4 py-3">Source Value</th><th className="px-4 py-3">PRIME Value</th><th className="px-4 py-3">Issue</th><th className="px-4 py-3">Action</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{!latest ? <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No reconciliation has been run yet.</td></tr> : rows.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No reconciliation rows for this filter.</td></tr> : rows.map((item, idx) => <tr key={`${item.externalProductId}-${idx}`} onClick={() => setSelected(item)} className="cursor-pointer hover:bg-slate-50"><td className="px-4 py-3"><Badge value={item.status} /></td><td className="px-4 py-3">{item.productCode || item.externalProductId || '-'}</td><td className="px-4 py-3">{item.productName || '-'}</td><td className="px-4 py-3">{item.source?.price ?? item.source?.name ?? '-'}</td><td className="px-4 py-3">{item.prime?.price ?? item.prime?.name ?? '-'}</td><td className="px-4 py-3">{item.issue || '-'}</td><td className="px-4 py-3">{item.status === 'MISSING_IN_PRIME' ? 'Preview Import' : item.status === 'MISSING_IN_SOURCE' ? 'Review' : '-'}</td></tr>)}</tbody>
        </table>
      </div>
      {selected?.diff?.length ? <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="mb-3 flex items-center justify-between"><div className="font-bold">Product {selected.productCode || selected.externalProductId}</div><button onClick={() => setSelected(null)} className="text-sm font-semibold text-slate-500">Close</button></div>{selected.diff.map((d) => <div key={d.field} className="grid grid-cols-3 gap-3 border-t border-slate-100 py-2 text-sm"><b>{d.field}</b><span>{String(d.incoming ?? '-')}</span><span className="font-semibold text-[#12284A]">{String(d.current ?? '-')}</span></div>)}</div> : null}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white"><table className="w-full min-w-[760px] text-left text-sm"><thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Timestamp</th><th className="px-4 py-3">Trigger</th><th className="px-4 py-3">Match</th><th className="px-4 py-3">Drift</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Action</th></tr></thead><tbody className="divide-y divide-slate-100">{history?.length ? history.map((h) => <tr key={h.reconciliation_id}><td className="px-4 py-3">{fmtDate(h.started_at || h.created_at)}</td><td className="px-4 py-3">{h.trigger_type}</td><td className="px-4 py-3">{h.match_rate}%</td><td className="px-4 py-3">{h.drift_count}</td><td className="px-4 py-3"><Badge value={h.status} /></td><td className="px-4 py-3"><button onClick={() => onLoad(h.reconciliation_id)} className="text-xs font-bold text-[#12284A]">View Differences</button></td></tr>) : <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No reconciliation history available yet.</td></tr>}</tbody></table></div>
    </div>
  );
}

function HistoryPanel({ history, busy, onRetry }) {
  const [operation, setOperation] = React.useState('ALL');
  const [result, setResult] = React.useState('ALL');
  const filtered = (history || []).filter((h) => {
    const opOk = operation === 'ALL'
      || (operation === 'MANUAL_SYNC' && !['SCHEDULED', 'MANUAL_RUN'].includes(h.trigger_type))
      || (operation === 'SCHEDULED_SYNC' && ['SCHEDULED', 'MANUAL_RUN'].includes(h.trigger_type))
      || (operation === 'RECONCILIATION' && String(h.operation || '').includes('RECONCILIATION'));
    const resultOk = result === 'ALL'
      || (result === 'SUCCESS' && h.status === 'SUCCESS')
      || (result === 'PARTIAL' && h.status === 'PARTIAL_SUCCESS')
      || (result === 'FAILED' && String(h.status || '').includes('FAILED'))
      || (result === 'REQUIRES_REVIEW' && ['ANOMALY_DETECTED', 'REQUIRES_REVIEW'].includes(h.status));
    return opOk && resultOk;
  });
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <select className={field} value={operation} onChange={(e) => setOperation(e.target.value)}><option value="ALL">All Operations</option><option value="MANUAL_SYNC">Manual Sync</option><option value="SCHEDULED_SYNC">Scheduled Sync</option><option value="RECONCILIATION">Reconciliation</option></select>
        <select className={field} value={result} onChange={(e) => setResult(e.target.value)}><option value="ALL">All Results</option><option value="SUCCESS">Success</option><option value="PARTIAL">Partial</option><option value="FAILED">Failed</option><option value="REQUIRES_REVIEW">Requires Review</option></select>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white"><table className="w-full min-w-[900px] text-left text-sm"><thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Timestamp</th><th className="px-4 py-3">Operation</th><th className="px-4 py-3">Trigger</th><th className="px-4 py-3">Triggered By</th><th className="px-4 py-3">Received</th><th className="px-4 py-3">Inserted</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3">Failed</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Action</th></tr></thead><tbody className="divide-y divide-slate-100">{filtered.length ? filtered.map((h) => <tr key={h.sync_id}><td className="px-4 py-3">{fmtDate(h.started_at || h.created_at)}</td><td className="px-4 py-3">{h.operation}</td><td className="px-4 py-3">{h.trigger_type || '-'}</td><td className="px-4 py-3">{h.triggered_by_name || h.triggered_by || '-'}</td><td className="px-4 py-3">{h.received}</td><td className="px-4 py-3">{h.inserted}</td><td className="px-4 py-3">{h.updated}</td><td className="px-4 py-3">{h.failed}</td><td className="px-4 py-3"><Badge value={h.status} /></td><td className="px-4 py-3"><button disabled={busy || !['FAILED', 'PARTIAL_SUCCESS', 'ANOMALY_DETECTED'].includes(h.status)} onClick={() => onRetry(h.sync_id)} className="text-xs font-bold text-[#12284A] disabled:text-slate-400">Retry</button></td></tr>) : <tr><td colSpan="10" className="px-4 py-10 text-center text-slate-500">No sync history for this filter.</td></tr>}</tbody></table></div>
    </div>
  );
}

function MemberDetail({ detail, preview, reconciliationItems, busy, onBack, onTest, onPreview, onCommit, onRetry, onScheduleSave, onSchedulePause, onScheduleResume, onRunNow, onRunReconciliation, onLoadReconciliation }) {
  const navigate = useNavigate();
  const [active, setActive] = React.useState('Overview');
  const tabs = ['Overview', 'Sync', 'Products', 'Mapping Issues', 'Reconciliation', 'History', 'Configuration'];
  const i = detail.integration;
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap justify-between gap-3">
          <div><button onClick={onBack} className="mb-2 text-sm font-semibold text-slate-500">All Member Integrations</button><h2 className="text-2xl font-bold">{i.member.code}</h2><p className="text-sm text-slate-600">{i.member.name}</p></div>
          <div className="flex flex-wrap items-start gap-2">
            <Badge value={i.health} /><Badge value={i.status} />
            <button onClick={() => navigate(`/admin/internal-tools/api-documentation/${i.member.code}?tab=guide`)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Integration Guide</button>
            <button onClick={() => navigate(`/admin/internal-tools/purchase-simulator?member=${i.member.code}`)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Open Purchase Simulator</button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((tab) => <button key={tab} onClick={() => setActive(tab)} className={`px-3 py-2 text-sm font-bold ${active === tab ? 'border-b-2 border-[#12284A] text-[#12284A]' : 'text-slate-500'}`}>{tab}</button>)}
      </div>
      {active === 'Overview' && <div className="grid gap-3 md:grid-cols-3">{[
        ['Connection', i.connectionStatus || '-'], ['Integration status', i.status], ['Transport', i.transport || '-'], ['Environment', i.environment || '-'], ['Adapter', i.adapter || '-'], ['Sync Mode', i.syncMode], ['Schedule', detail.schedule?.is_enabled ? detail.schedule.frequency_type : 'Disabled'], ['Next Run', fmtDate(detail.schedule?.next_run_at)], ['Last Reconciliation', fmtDate(detail.reconciliation?.started_at)], ['Match Rate', detail.reconciliation?.match_rate != null ? `${detail.reconciliation.match_rate}%` : '-'], ['Consecutive Failures', detail.schedule?.consecutive_failure_count || 0], ['Product Count', i.stats?.product_count], ['Last Sync', fmtDate(i.timestamps?.last_sync_at)], ['Last Successful Sync', fmtDate(i.timestamps?.last_success_at)], ['Last Failed Sync', fmtDate(i.timestamps?.last_failure_at)], ['Valid Products', i.stats?.mapped_count], ['Unmapped Products', i.stats?.unresolved_count], ['Duplicate External IDs', detail.reconciliation?.duplicate_count || 0], ['Integration Health', i.health],
      ].map(([k, v]) => <div key={k} className="rounded-lg border border-slate-200 bg-white p-4"><div className="text-[11px] font-bold uppercase text-slate-500">{k}</div><div className="mt-1 font-semibold">{v ?? '-'}</div></div>)}</div>}
      {active === 'Sync' && <div className="space-y-4"><div className="flex flex-wrap gap-2"><button disabled={busy || !i.connectionTestSupported} onClick={onTest} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold disabled:opacity-40"><PlayCircle className="h-4 w-4" />{busy ? 'Testing...' : 'Test Connection'}</button><button disabled={busy || !i.previewSupported} onClick={onPreview} className="rounded-md bg-[#12284A] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">{busy ? 'Fetching...' : 'Preview Sync'}</button>{preview && <button disabled={busy} onClick={onCommit} className="rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">{busy ? 'Syncing...' : 'Commit Sync'}</button>}</div>{preview ? <PreviewTable preview={preview} /> : <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Run Preview Sync to compare Member source data with PRIME before writing anything.</div>}</div>}
      {active === 'Products' && <div className="overflow-hidden rounded-lg border border-slate-200 bg-white"><table className="w-full min-w-[760px] text-left text-sm"><thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Mapping</th><th className="px-4 py-3">Last Sync</th><th className="px-4 py-3">Source</th></tr></thead><tbody className="divide-y divide-slate-100">{detail.products?.length ? detail.products.map((p) => <tr key={`${p.source}-${p.id}`}><td className="px-4 py-3 font-semibold">{p.code}</td><td className="px-4 py-3">{p.name}</td><td className="px-4 py-3">{p.mappingStatus}</td><td className="px-4 py-3">{fmtDate(p.lastSyncAt)}</td><td className="px-4 py-3">{p.resolverSource || p.source}</td></tr>) : <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">No products synced yet.</td></tr>}</tbody></table></div>}
      {active === 'Mapping Issues' && <PreviewTable preview={{ items: (preview?.items || []).filter((x) => ['INVALID', 'UNMAPPED'].includes(x.status)), counts: preview?.counts || {} }} />}
      {active === 'Reconciliation' && <ReconciliationPanel latest={detail.reconciliation} history={detail.reconciliationHistory || []} items={reconciliationItems || []} busy={busy} onRun={onRunReconciliation} onLoad={onLoadReconciliation} />}
      {active === 'History' && <HistoryPanel history={detail.history || []} busy={busy} onRetry={onRetry} />}
      {active === 'Configuration' && <div className="space-y-4"><SchedulePanel schedule={detail.schedule} busy={busy} onSave={onScheduleSave} onPause={onSchedulePause} onResume={onScheduleResume} onRunNow={onRunNow} /><div className="rounded-lg border border-slate-200 bg-white p-4"><div className="mb-3 font-bold">Safe Configuration</div>{detail.contracts?.flatMap((c) => c.configKeys || []).map((cfg) => <div key={cfg.key} className="flex justify-between border-t border-slate-100 py-2 text-sm"><span>{cfg.key}</span><b>{cfg.status}</b></div>) || <div className="text-sm text-slate-500">No configuration metadata documented.</div>}</div></div>}
    </div>
  );
}

export default function MemberIntegration() {
  const navigate = useNavigate();
  const { memberCode } = useParams();
  const { user } = useAuth();
  const allowed = normalizeAdminStatus(user) === 'admin_inj';
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [list, setList] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [needsAttention, setNeedsAttention] = React.useState([]);
  const [detail, setDetail] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [reconciliationItems, setReconciliationItems] = React.useState([]);
  const [notice, setNotice] = React.useState('');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError('');
    try {
      if (memberCode) {
        const res = await channelApi.get(`/admin/member-integrations/${memberCode}`);
        setDetail(res.data?.data || null);
        const latest = res.data?.data?.reconciliation;
        if (latest?.reconciliation_id) {
          try {
            const rec = await channelApi.get(`/admin/member-integrations/${memberCode}/reconciliation/${latest.reconciliation_id}`);
            setReconciliationItems(rec.data?.meta?.items || []);
          } catch (_) { setReconciliationItems([]); }
        } else {
          setReconciliationItems([]);
        }
      } else {
        const res = await channelApi.get('/admin/member-integrations');
        setList(res.data?.data || []);
        setSummary(res.data?.meta?.summary || null);
        setNeedsAttention(res.data?.meta?.needsAttention || []);
      }
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [allowed, memberCode]);

  React.useEffect(() => { load(); }, [load]);

  const runTest = async (code = memberCode) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${code}/test-connection`);
      const d = res.data?.data;
      setNotice(d.supported === false ? d.message : `${d.member.code} connection ${d.status}${d.latencyMs != null ? ` (${d.latencyMs} ms)` : ''}`);
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const runPreview = async (code = memberCode) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${code}/preview`, {});
      setPreview(res.data?.data || null);
      setNotice(`Preview ready: ${res.data?.data?.counts?.received ?? 0} received.`);
      if (!memberCode) navigate(`/admin/internal-tools/member-integration/${code}`);
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (!preview) return;
    const c = preview.counts || {};
    const ok = window.confirm(`Commit ${preview.member.code} Product Sync?\n\n${c.new || 0} new products\n${c.update || 0} products will be updated\n${c.potentialDeactivation || 0} products may be marked inactive\n${(c.invalid || 0) + (c.unmapped || 0)} records will be skipped because of issues`);
    if (!ok) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${preview.member.code}/previews/${preview.previewId}/commit`);
      setNotice(`Sync completed: ${res.data?.data?.status}`);
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const runRetry = async (syncId) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${memberCode}/history/${syncId}/retry`);
      setPreview(res.data?.data || null);
      setNotice(`Retry preview created: ${res.data?.data?.previewId}`);
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const saveSchedule = async (form) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await channelApi.put(`/admin/member-integrations/${memberCode}/schedule`, form);
      setNotice('Schedule updated.');
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const pauseSchedule = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await channelApi.post(`/admin/member-integrations/${memberCode}/schedule/pause`);
      setNotice('Schedule paused.');
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const resumeSchedule = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await channelApi.post(`/admin/member-integrations/${memberCode}/schedule/resume`);
      setNotice('Schedule resumed.');
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${memberCode}/run-now`);
      setNotice(`Run Now completed: ${res.data?.data?.status}`);
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const runReconciliation = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.post(`/admin/member-integrations/${memberCode}/reconciliation/run`);
      setReconciliationItems(res.data?.meta?.items || []);
      setNotice(`Reconciliation completed: ${res.data?.data?.status}`);
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  const loadReconciliation = async (id) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await channelApi.get(`/admin/member-integrations/${memberCode}/reconciliation/${id}`);
      setReconciliationItems(res.data?.meta?.items || []);
      setNotice(`Loaded reconciliation: ${id}`);
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  if (!allowed) return <div className="mx-auto max-w-3xl rounded-lg border border-red-100 bg-white p-8"><h1 className="text-xl font-bold">Akses dibatasi</h1><p className="mt-2 text-slate-600">Member Integration hanya tersedia untuk Admin INJ.</p></div>;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div><div className="text-xs font-semibold uppercase tracking-[.18em] text-slate-500">Internal Tools</div><h1 className="mt-1 text-2xl font-bold text-slate-900">PRIME Member Sync Control Center</h1><p className="mt-1 text-sm text-slate-600">Kelola koneksi, preview, sinkronisasi, dan monitoring integrasi Member ke PRIME.</p></div>
      {notice && <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div>}
      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!memberCode ? (
        <MemberList data={list} summary={summary} needsAttention={needsAttention} loading={loading} onOpen={(code) => navigate(`/admin/internal-tools/member-integration/${code}`)} onTest={runTest} onPreview={runPreview} />
      ) : !detail ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">{loading ? 'Loading member detail...' : 'Member integration not found.'}</div>
      ) : (
        <MemberDetail detail={detail} preview={preview} reconciliationItems={reconciliationItems} busy={busy} onBack={() => navigate('/admin/internal-tools/member-integration')} onTest={() => runTest(memberCode)} onPreview={() => runPreview(memberCode)} onCommit={runCommit} onRetry={runRetry} onScheduleSave={saveSchedule} onSchedulePause={pauseSchedule} onScheduleResume={resumeSchedule} onRunNow={runNow} onRunReconciliation={runReconciliation} onLoadReconciliation={loadReconciliation} />
      )}
    </div>
  );
}
